import { memo, useState } from 'react';
import { Response } from '@/components/ui/shadcn-io/ai/response';
import { TodosQueue } from '@/components/ui/shadcn-io/ai/queue';
import { AtomLoader } from '@/components/ui/atom-loader';
import { ContentSegment, ParsedTool } from './message-parser';
import { SelectionOptionTiles } from './selection-option-tiles';
import { AlertCircle, Loader2Icon } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { SelectionOption, SelectionRequest } from '@/types/command-selection';

interface AssistantMessageContentProps {
    content: string;
    contentSegments?: ContentSegment[];
    tools?: ParsedTool[];
    isStreaming?: boolean;
    messageId: string;
    status?: string;
    streamError?: string;
    selectionDisabled?: boolean;
    onPickSelection?: (option: SelectionOption, request: SelectionRequest) => void;
}

export function getAssistantErrorText(
    contentSegments?: ContentSegment[],
    streamError?: string
): string | undefined {
    if (streamError?.trim()) return streamError;
    return contentSegments?.find((segment) => segment.type === 'error')?.content;
}

function MessageErrorBanner({ text }: { text: string }) {
    return (
        <div
            role="alert"
            className="flex w-full items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <Response className="min-w-0 flex-1 break-words [&_p]:m-0 [&_strong]:font-semibold">
                {text}
            </Response>
        </div>
    );
}

/**
 * Clean streaming content - remove code block markers and other artifacts
 */


/**
 * Clean tool output - remove code block markers
 */
function cleanToolOutput(output: string): string {
    return output;
}

/**
 * Convert Python dict string to valid JSON
 * Handles: single quotes, None, True, False, escaped quotes, and nested apostrophes
 */
function pythonDictToJson(pythonStr: string): string {
    let result = '';
    let inString = false;
    let stringChar = '';
    let i = 0;

    while (i < pythonStr.length) {
        const char = pythonStr[i];
        const nextChar = pythonStr[i + 1];

        if (!inString) {
            if (char === "'" || char === '"') {
                inString = true;
                stringChar = char;
                result += '"'; // Always use double quotes
            } else if (pythonStr.slice(i, i + 4) === 'None') {
                result += 'null';
                i += 3;
            } else if (pythonStr.slice(i, i + 4) === 'True') {
                result += 'true';
                i += 3;
            } else if (pythonStr.slice(i, i + 5) === 'False') {
                result += 'false';
                i += 4;
            } else {
                result += char;
            }
        } else {
            // Inside a string
            if (char === '\\' && nextChar === '"') {
                // Already escaped double quote - keep it escaped
                result += '\\"';
                i++;
            } else if (char === '\\' && nextChar === "'") {
                // Escaped single quote - just output the single quote
                result += "'";
                i++;
            } else if (char === '\\' && nextChar === '\\') {
                // Escaped backslash
                result += '\\\\';
                i++;
            } else if (char === '\\' && nextChar) {
                // Other escape sequence - keep it
                result += char + nextChar;
                i++;
            } else if (char === stringChar) {
                // End of string
                inString = false;
                result += '"';
            } else if (char === '"' && stringChar === "'") {
                // Double quote inside single-quoted string - escape it
                result += '\\"';
            } else if (char === "'" && stringChar === '"') {
                // Single quote (apostrophe) inside double-quoted string - keep as is
                result += "'";
            } else {
                result += char;
            }
        }
        i++;
    }

    return result;
}

/**
 * Pre-process string to normalize escape sequences
 */
function normalizeEscapes(str: string): string {
    // Handle mixed escaping: \" inside single-quoted strings
    // This pattern: 'text \"quoted\" text' should become "text \"quoted\" text"
    return str
        // First, temporarily replace \\" with a placeholder
        .replace(/\\\\"/g, '<<<ESCAPED_QUOTE>>>')
        // Replace \" that's inside strings
        .replace(/\\"([^"]*?)\\"/g, '"$1"')
        // Restore escaped quotes
        .replace(/<<<ESCAPED_QUOTE>>>/g, '\\"');
}

/**
 * Try to parse and format JSON from observation content
 * Python dict format: {'key': 'value'} -> {"key": "value"}
 */
function tryParseObservationJson(content: string): any | null {
    if (!content) return null;

    const cleaned = content.trim();

    // First try direct JSON parse
    try {
        return JSON.parse(cleaned);
    } catch {
        // Not valid JSON
    }

    // Pre-process to normalize escape sequences
    const normalized = normalizeEscapes(cleaned);

    // Try converting Python dict format to JSON
    try {
        const jsonStr = pythonDictToJson(normalized);
        return JSON.parse(jsonStr);
    } catch {
        // Still not valid
    }

    // Try with original string
    try {
        const jsonStr = pythonDictToJson(cleaned);
        return JSON.parse(jsonStr);
    } catch {
        // Still not valid
    }

    // Try a regex-based approach for simple cases
    try {
        let simple = cleaned
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        // Replace single quotes with double quotes more carefully
        // Match string boundaries
        simple = simple.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (match, content) => {
            // Escape any unescaped double quotes in the content
            const escaped = content.replace(/(?<!\\)"/g, '\\"');
            return `"${escaped}"`;
        });

        return JSON.parse(simple);
    } catch {
        return null;
    }
}

/**
 * Format JSON value for display with syntax highlighting
 */
function formatJsonValue(value: any, indent: number = 0): React.ReactNode {
    const indentStr = '  '.repeat(indent);

    if (value === null) {
        return <span className="text-orange-500 dark:text-orange-400">null</span>;
    }

    if (typeof value === 'boolean') {
        return <span className="text-purple-600 dark:text-purple-400">{value ? 'true' : 'false'}</span>;
    }

    if (typeof value === 'number') {
        return <span className="text-blue-600 dark:text-blue-400">{value}</span>;
    }

    if (typeof value === 'string') {
        // Truncate long strings
        const displayStr = value.length > 100 ? value.slice(0, 100) + '...' : value;
        return <span className="text-green-600 dark:text-green-400">&quot;{displayStr}&quot;</span>;
    }

    if (Array.isArray(value)) {
        if (value.length === 0) return <span>[]</span>;
        return (
            <span>
                {'[\n'}
                {value.map((item, idx) => (
                    <span key={idx}>
                        {indentStr}  {formatJsonValue(item, indent + 1)}
                        {idx < value.length - 1 ? ',\n' : '\n'}
                    </span>
                ))}
                {indentStr}{']'}
            </span>
        );
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value);
        if (entries.length === 0) return <span>{'{}'}</span>;
        return (
            <span>
                {'{\n'}
                {entries.map(([key, val], idx) => (
                    <span key={key}>
                        {indentStr}  <span className="text-sky-600 dark:text-sky-400">&quot;{key}&quot;</span>: {formatJsonValue(val, indent + 1)}
                        {idx < entries.length - 1 ? ',\n' : '\n'}
                    </span>
                ))}
                {indentStr}{'}'}
            </span>
        );
    }

    return String(value);
}

/**
 * Render formatted tool output - detects JSON and formats it nicely
 */
function FormattedToolOutput({ output }: { output: string }) {
    const cleaned = cleanToolOutput(output);
    const parsed = tryParseObservationJson(cleaned);

    if (parsed) {
        const hasData = parsed.data !== undefined;
        const hasError = parsed.error && parsed.error !== null;

        return (
            <div>
                {/* Main data */}
                <div className="font-mono text-xs p-4 overflow-x-auto whitespace-pre text-foreground">
                    {hasData ? formatJsonValue(parsed.data) : formatJsonValue(parsed)}
                </div>

                {/* Error message if any */}
                {hasError && (
                    <div className="text-xs text-destructive border-t border-border p-3">
                        {String(parsed.error)}
                    </div>
                )}
            </div>
        );
    }

    // Not JSON, show as plain text with nice formatting
    return (
        <div className="font-mono text-xs p-4 overflow-x-auto whitespace-pre-wrap break-words text-foreground">
            {cleaned}
        </div>
    );
}

/** Single tool call rendered as compact Request/Response card */
function ToolCallBlock({ tool, isStreaming }: { tool: ParsedTool; isStreaming?: boolean }) {
    const [requestOpen, setRequestOpen] = useState(false);
    const [responseOpen, setResponseOpen] = useState(false);
    const isFailed = tool.state === 'output-error';
    
    const isExecuting =
        isStreaming === true &&
        (tool.state === 'input-streaming' || tool.state === 'input-available');
    const isCompleted =
        tool.state === 'output-available' ||
        (!isStreaming && !isFailed && (tool.state === 'input-available' || tool.state === 'input-streaming'));
    const hasInput = tool.input && Object.keys(tool.input).length > 0;
    const hasOutput = tool.output != null && tool.output !== '';
    const hasResponse = hasOutput || Boolean(tool.errorText);
    const displayToolName = tool.type
        .replace(/^tool[_-]?/i, '')
        .replace(/[_-]+/g, ' ')
        .toUpperCase();
    const statusLabel = isFailed ? 'Failed' : isCompleted ? 'Success' : isExecuting ? 'Running' : 'Pending';
    const statusClass = isFailed
        ? 'text-destructive'
        : isCompleted
          ? 'text-emerald-700 dark:text-emerald-400'
          : isExecuting
            ? 'text-green-500 dark:text-green-400'
            : 'text-muted-foreground';

    const open = requestOpen || responseOpen;
    const handleRowToggle = () => {
        if (open) {
            setRequestOpen(false);
            setResponseOpen(false);
            return;
        }
        if (hasInput) {
            setRequestOpen(true);
        } else if (hasResponse) {
            setResponseOpen(true);
        }
    };

    return (
        <div className="tool-call-container mt-3 space-y-2">
            <Collapsible open={open}>
                <div
                    className="tool-call-row flex cursor-pointer flex-col gap-2 rounded-2xl border border-border bg-background px-4 py-3"
                    onClick={handleRowToggle}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-xs font-semibold tracking-wide text-foreground">
                            {displayToolName}
                        </span>
                        <span className={cn('shrink-0 text-xs font-medium', statusClass)}>
                            {statusLabel}
                        </span>
                        {isExecuting && <Loader2Icon className="size-3 shrink-0 animate-spin text-green-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasInput) setRequestOpen((prev) => !prev);
                            }}
                            className={cn(
                                'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted',
                                requestOpen && 'bg-muted text-foreground',
                                !hasInput && 'cursor-not-allowed opacity-50 hover:bg-transparent'
                            )}
                            disabled={!hasInput}
                        >
                            Request
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasResponse) setResponseOpen((prev) => !prev);
                            }}
                            className={cn(
                                'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted',
                                responseOpen && 'bg-muted text-foreground',
                                !hasResponse && 'cursor-not-allowed opacity-50 hover:bg-transparent'
                            )}
                            disabled={!hasResponse}
                        >
                            Response
                        </button>
                    </div>
                </div>

                <CollapsibleContent className="mt-2 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-1 data-[state=open]:slide-in-from-top-1">
                    <div className="space-y-2 rounded-2xl border border-border bg-background p-3">
                        {requestOpen && hasInput && (
                            <div className="max-h-[360px] overflow-auto rounded-lg bg-muted/50 text-xs">
                                <div className="px-3 pt-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
                                    REQUEST
                                </div>
                                <FormattedToolOutput output={JSON.stringify(tool.input, null, 2)} />
                            </div>
                        )}
                        {responseOpen && hasOutput && (
                            <div className="max-h-[360px] overflow-auto rounded-lg bg-muted/50 text-xs">
                                <div className="px-3 pt-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
                                    RESPONSE
                                </div>
                                <FormattedToolOutput output={String(tool.output)} />
                            </div>
                        )}
                        {responseOpen && tool.errorText && (
                            <div className="text-xs text-destructive bg-destructive/10 rounded-lg p-2">
                                {tool.errorText}
                            </div>
                        )}
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}

/**
 * Render assistant message content with proper formatting
 * Uses contentSegments for ordered display - same format for streaming and database
 * During streaming: shows text as-is (no cleaning)
 * After streaming: cleans text (removes backticks, etc.)
 */
export const AssistantMessageContent = memo(function AssistantMessageContent({
    content,
    contentSegments,
    tools,
    isStreaming,
    messageId,
    status,
    streamError,
    selectionDisabled,
    onPickSelection,
}: AssistantMessageContentProps) {
    const errorText = getAssistantErrorText(contentSegments, streamError);

    if (errorText && (!contentSegments || contentSegments.length === 0)) {
        return <MessageErrorBanner text={errorText} />;
    }

    // Use contentSegments for both streaming and loaded messages
    if (contentSegments && contentSegments.length > 0) {
        const hasErrorSegment = contentSegments.some((segment) => segment.type === 'error');
        let lastWriteTodosSegmentIndex = -1;
        for (let i = 0; i < contentSegments.length; i++) {
            const s = contentSegments[i];
            if (s.type === 'tool' && s.tool?.type === 'tool-write_todos') {
                lastWriteTodosSegmentIndex = i;
            }
        }

        return (
            <>
                {contentSegments.map((segment, idx) => {
                    if (segment.type === 'error' && segment.content) {
                        return (
                            <MessageErrorBanner
                                key={`error-${messageId}-${idx}`}
                                text={segment.content}
                            />
                        );
                    }
                    if (segment.type === 'text' && segment.content) {
                        // During streaming: show as-is for typing effect
                        // After streaming: clean the content
                        const displayText = isStreaming
                            ? segment.content
                            : segment.content;

                        if (!displayText) return null;
                        return (
                            <Response
                                key={`text-${idx}`}
                                className='break-words overflow-wrap-anywhere'
                                parseIncompleteMarkdown={Boolean(isStreaming)}
                            >
                                {displayText}
                            </Response>
                        );
                    } else if (segment.type === 'tool' && segment.tool) {
                        // Choice tool → option tiles (same UI as selection_request blocks)
                        if (
                            segment.tool.type === 'tool-aios_ask_user_choice' ||
                            /ask_user_choice/i.test(segment.tool.type)
                        ) {
                            const input = (segment.tool.input ?? {}) as Record<string, unknown>;
                            const rawOptions = Array.isArray(input.options) ? input.options : [];
                            const options: SelectionOption[] = [];
                            for (const opt of rawOptions) {
                                if (!opt || typeof opt !== 'object') continue;
                                const record = opt as Record<string, unknown>;
                                const value = String(record.value ?? record.id ?? '').trim();
                                const label = String(record.label ?? record.name ?? value).trim();
                                if (!label) continue;
                                const subtitle =
                                    typeof record.subtitle === 'string'
                                        ? record.subtitle
                                        : typeof record.description === 'string'
                                          ? record.description
                                          : undefined;
                                options.push({
                                    value: value || label,
                                    label,
                                    ...(subtitle ? { subtitle } : {}),
                                    recommended: Boolean(record.recommended),
                                });
                            }

                            if (options.length > 0 && onPickSelection) {
                                const request: SelectionRequest = {
                                    toolCallId: segment.tool.toolCallId,
                                    name: segment.tool.type,
                                    prompt: typeof input.prompt === 'string' ? input.prompt : undefined,
                                    options,
                                    multiSelect: Boolean(input.multi_select),
                                };
                                return (
                                    <SelectionOptionTiles
                                        key={`selection-tool-${segment.tool.toolCallId ?? idx}`}
                                        selection={request}
                                        disabled={selectionDisabled}
                                        onPick={(option) => onPickSelection(option, request)}
                                    />
                                );
                            }
                        }
                        // Render write_todos as Queue UI instead of generic tool
                        if (segment.tool.type === 'tool-write_todos') {
                            const todos = (segment.tool.input?.todos ?? []).map((t: any, i: number) => ({
                                id: t.id ?? String(i),
                                title: t.content ?? t.title ?? '',
                                status: t.status ?? 'pending',
                            }));
                            const isLatestWriteTodos = idx === lastWriteTodosSegmentIndex;
                            return (
                                <div
                                    key={`${segment.tool.toolCallId ?? `tool-${idx}`}-latest-${isLatestWriteTodos}`}
                                    className="mt-4 w-full max-w-full"
                                >
                                    <TodosQueue todos={todos} defaultOpen={isLatestWriteTodos} />
                                </div>
                            );
                        }
                        return (
                            <ToolCallBlock
                                key={segment.tool.toolCallId || `tool-${idx}`}
                                tool={segment.tool}
                                isStreaming={isStreaming}
                            />
                        );
                    } else if (segment.type === 'selection' && segment.selection && onPickSelection) {
                        const request = segment.selection;
                        return (
                            <SelectionOptionTiles
                                key={`selection-${messageId}-${idx}`}
                                selection={request}
                                disabled={selectionDisabled}
                                onPick={(option) => onPickSelection(option, request)}
                            />
                        );
                    }
                    return null;
                })}
                {isStreaming && !hasErrorSegment && (
                    <AtomLoader size="md" variant="minimal" />
                )}
            </>
        );
    }

    // Streaming with no segments yet — show the atom loader
    if (isStreaming) {
        return <AtomLoader size="md" variant="minimal" />;
    }

    // Fallback: Show raw content if no segments (for simple text messages)

        
        if (content) {
            return (
                <Response
                    className='break-words overflow-wrap-anywhere'
                    parseIncompleteMarkdown={Boolean(isStreaming)}
                >
                    {content}
                </Response>
            );
        }

    return null;
}, (prevProps, nextProps) => {
    const segmentSignature = (segments?: ContentSegment[]) =>
        (segments ?? [])
            .map((seg) => {
                if (seg.type === 'error') return `e:${seg.content ?? ''}`;
                if (seg.type === 'text') return `t:${seg.content ?? ''}`;
                if (seg.type === 'selection') {
                    const opts = seg.selection?.options ?? [];
                    return `s:${opts.map((o) => o.value).join(',')}`;
                }
                const tool = seg.tool;
                return `u:${tool?.toolCallId ?? ''}:${tool?.state ?? ''}:${tool?.type ?? ''}:${tool?.output ?? ''}`;
            })
            .join('|');

    const toolsSignature = (toolList?: ParsedTool[]) =>
        (toolList ?? [])
            .map((tool) => `${tool.toolCallId}:${tool.state}:${tool.type}:${tool.output ?? ''}`)
            .join('|');

    // Always re-render during streaming to show typing effect
    if (nextProps.isStreaming) return false;

    // After streaming, compare normally
    if (prevProps.isStreaming !== nextProps.isStreaming) return false;
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.streamError !== nextProps.streamError) return false;
    if (prevProps.messageId !== nextProps.messageId) return false;
    if (prevProps.selectionDisabled !== nextProps.selectionDisabled) return false;
    if (prevProps.onPickSelection !== nextProps.onPickSelection) return false;
    if (prevProps.contentSegments?.length !== nextProps.contentSegments?.length) return false;
    if (segmentSignature(prevProps.contentSegments) !== segmentSignature(nextProps.contentSegments)) return false;
    if (toolsSignature(prevProps.tools) !== toolsSignature(nextProps.tools)) return false;

    return prevProps.content === nextProps.content;
});

